import type {
  ControlStatusResponseDto,
  ControlWorkflowStatsItemResponseDto,
} from "@/spz-api/model";
import { Hint } from "@spz/ui/components/hint";
import { PieChartDataLegend } from "@spz/ui/components/pie-chart-data-legend";
import {
  SEGMENT_VARIANTS,
  SegmentedProgressBar,
} from "@spz/ui/components/segmented-progress-bar";
import { Skeleton } from "@spz/ui/components/skeleton";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "@spz/ui/lib/utils";
import { t } from "i18next";
import type { FC } from "react";
import { useMemo } from "react";

interface ByStatusProps {
  workflowStats?: ControlWorkflowStatsItemResponseDto[];
  controlStatuses?: ControlStatusResponseDto[];
  totalCount?: number;
  loading?: boolean;
  error?: boolean;
  className?: string;
}

const ByStatusChart: FC<ByStatusProps> = ({
  workflowStats,
  controlStatuses,
  totalCount = 0,
  loading = false,
  error = false,
  className,
}) => {
  const statusSegments = useMemo(() => {
    if (!controlStatuses || !workflowStats) return [];

    const computePercentage = (count: number) =>
      totalCount > 0 ? (count / totalCount) * 100 : 0;

    return workflowStats.map((step) => {
      const stepDetails = controlStatuses.find((s) => s.id === step.stepId);
      return {
        key: stepDetails?.id ?? "unknown",
        count: step.count,
        percentage: computePercentage(step.count),
        variant: stepDetails?.variant ?? "gray",
        labelKey: stepDetails?.labelKeyName || "unknown_status",
      };
    });
  }, [workflowStats, controlStatuses, totalCount]);

  const byStatusLegendData = useMemo(() => {
    if (!statusSegments.length) return {};

    return statusSegments.reduce(
      (acc, segment) => {
        const label = t(segment.labelKey);

        acc[label] = {
          value: Math.round(segment.percentage * 10) / 10,
          color: SEGMENT_VARIANTS[segment.variant] || "#e5e7eb",
        };

        return acc;
      },
      {} as Record<string, { value: number; color: string }>,
    );
  }, [statusSegments]);

  const hasData =
    statusSegments.length > 0 &&
    statusSegments.some((segment) => segment.count > 0);

  return (
    <div
      className={cn(
        "bg-white p-4",
        "container rounded justify-between",
        "flex flex-col",
        "h-full",
        className,
      )}
    >
      <Typography variant="h6" trans="distribution_by_status" />

      {loading ? (
        <div className="flex flex-col gap-4 h-full justify-between">
          <Skeleton className="w-full h-[11px] mt-6" />
          <div className="flex flex-col gap-4">
            <Skeleton className="w-full h-3" />
            <Skeleton className="w-full h-3" />
            <Skeleton className="w-full h-3" />
            <Skeleton className="w-full h-3" />
          </div>
        </div>
      ) : !hasData ? (
        <Hint text={t("no_results")} horizontal={false} className="m-auto" />
      ) : error ? (
        <Hint
          text={t("error_loading_data")}
          horizontal={false}
          className="m-auto"
          variant="warning"
        />
      ) : (
        <>
          <SegmentedProgressBar
            segments={statusSegments}
            displayTooltip={false}
            height={11}
            className="w-full"
          />

          <PieChartDataLegend data={byStatusLegendData} />
        </>
      )}
    </div>
  );
};

export default ByStatusChart;
